/**
 * "Not machine-read — for your review": the labelled lines the grammar
 * recognised and could not parse safely, on EVERY intake record (UI-07).
 *
 * The list used to render only for an upload made in the current browser
 * session; a mailbox record surfaced its lines only as near-miss evidence on
 * the 15 checklist fields, so "DSCR: 1.25x (in-place)" failed to parse and no
 * reviewer saw it. Each line now carries the two verbs its Data Review row
 * offers — Correct (open the row and type the value) and Not available (the
 * line states nothing usable) — once the promotion agent has written that row
 * (`dataReview.record`); until then the line is listed and says it is waiting.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'

import {
  dataReview,
  dataReviewKeys,
  invalidateAfterDecision,
  type DataReviewItem,
} from '@/services/dataReview'
import type { ParseReport } from '@/services/ingestion'

export type UnmatchedLine = NonNullable<ParseReport['unmatchedLines']>[number]

/** The reason recorded with "Not available" from this panel. Unratified
 *  wording (4 Nov 2026) — Edward to sign off. */
export const UNMATCHED_NOT_AVAILABLE_REASON =
  'Not available — a reviewer read the labeled line the engine could not parse and found no usable value in it'

function normalize(text: string | null | undefined): string {
  return (text ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

/** The Data Review row written for an unmatched line: a found datapoint or a
 *  checklist row whose label is the line's label. Null while none exists. */
export function reviewRowForLine(items: DataReviewItem[] | undefined, line: UnmatchedLine): DataReviewItem | null {
  if (!items?.length) return null
  const label = normalize(line.label)
  const text = normalize(line.text)
  if (!label && !text) return null
  const open = items.filter((item) => item.kind !== 'decided' && !item.humanDecided)
  const byLabel = (item: DataReviewItem) => {
    const found = item.detail?.discovered
    const names = [item.label, item.field, found?.label, found?.displayLabel, found?.proposedLabel]
      .map(normalize)
      .filter(Boolean)
    return Boolean(label) && names.includes(label)
  }
  return (
    open.find((item) => item.source === 'discovery' && byLabel(item))
    ?? open.find(byLabel)
    ?? open.find((item) => Boolean(text) && normalize(item.question).includes(text))
    ?? null
  )
}

export default function UnmatchedLinesPanel({
  ingestionId,
  lines,
  truncated = 0,
  demo = false,
  onOpenReview,
}: {
  ingestionId: string | null | undefined
  lines: UnmatchedLine[]
  /** Lines the server cut from the list, counted in the header. */
  truncated?: number | null
  /** A demo preview creates no record: the lines are listed with no verbs. */
  demo?: boolean
  /** Open the Data Review on this record — at the row when one exists — to correct the value. */
  onOpenReview?: (itemId: string | null, line: UnmatchedLine) => void
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const live = Boolean(ingestionId) && !demo && lines.length > 0
  const review = useQuery({
    queryKey: dataReviewKeys.record(ingestionId ?? ''),
    queryFn: () => dataReview.record(ingestionId as string),
    enabled: live,
    retry: false,
  })
  const decide = useMutation({
    mutationFn: (input: { itemId: string }) =>
      dataReview.decide({ itemId: input.itemId, verb: 'unavailable', reason: UNMATCHED_NOT_AVAILABLE_REASON }),
    onSuccess: async () => {
      setError(null)
      await invalidateAfterDecision(queryClient, review.data?.loanId ?? null)
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'The decision was not recorded. Please retry.')
    },
  })
  const rows = useMemo(
    () => lines.map((line) => ({ line, item: reviewRowForLine(review.data?.items, line) })),
    [lines, review.data],
  )

  if (lines.length === 0) return null
  const total = lines.length + (truncated ?? 0)

  return (
    <div className="panel" data-testid="unmatched-lines-panel">
      <div className="panel-header">
        <span className="panel-header-title">Not machine-read — for your review</span>
        <span className="text-[10px] text-stone-600" data-testid="unmatched-lines-count">
          {`${total} line${total === 1 ? '' : 's'}${live && review.isLoading ? ' · reading the review rows…' : ''}`}
        </span>
      </div>
      <div className="panel-body space-y-1">
        <ul className="max-h-48 space-y-1 overflow-y-auto scrollbar-thin">
          {rows.map(({ line, item }, i) => {
            const busy = decide.isPending && decide.variables?.itemId === item?.id
            return (
              <li key={`${line.source}-${line.lineNo ?? i}-${i}`} className="flex items-center gap-2" data-review-row={item?.id ?? undefined}>
                <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-stone-400" title={`${line.reason}${line.source ? ` · ${line.source}${line.lineNo != null ? ` line ${line.lineNo}` : ''}` : ''}`}>
                  {line.label ? <span className="text-stone-500">{line.label}: </span> : null}
                  {line.text}
                </span>
                {live ? (
                  <span className="flex shrink-0 items-center gap-1">
                    {onOpenReview ? (
                      <button
                        type="button"
                        onClick={() => onOpenReview(item?.id ?? null, line)}
                        className="rounded border border-amber-400/25 px-1.5 py-px text-[9px] text-amber-200 hover:bg-amber-400/10"
                        title={item ? 'Open this line’s Data Review row and type the value' : 'Open the Data Review on this record'}
                      >
                        Correct
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={!item || busy}
                      onClick={() => item && decide.mutate({ itemId: item.id })}
                      className="rounded border border-stone-700 px-1.5 py-px text-[9px] text-stone-300 hover:bg-stone-800 disabled:cursor-not-allowed disabled:text-stone-600"
                      title={item ? 'Record that this line states no usable value' : 'Awaiting its Data Review row'}
                    >
                      {busy ? <Loader2 size={9} className="inline animate-spin" /> : 'Not available'}
                    </button>
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
        {error ? (
          <p className="flex items-center gap-1 text-[9px] text-red-300">
            <AlertTriangle size={9} /> {error}
          </p>
        ) : null}
        <p className="text-[9px] text-stone-600">
          Labeled lines the grammar recognized but could not parse safely. Nothing here was guessed at.
          {live && review.isError ? ' The Data Review rows for this record could not be read.' : ''}
        </p>
      </div>
    </div>
  )
}
